import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

type SliderTokens = {
  label: string
  track: string
  range: string
  thumb: string
}

// Resolved to CSS vars rather than token names: the filled portion is painted
// with a linear-gradient, and a gradient stop has to be a real colour value.
const tokens: Record<ColorScheme, SliderTokens> = {
  dark: {
    label: tokenVar('colors.text.secondary'),
    // Same track surface ProgressBar uses, so a bar and a slider read as one
    // family of control.
    track: tokenVar('colors.bg.input'),
    range: tokenVar('colors.interactive.toggle-on'),
    thumb: tokenVar('colors.fg.toggle-knob'),
  },
  light: {
    label: tokenVar('colors.light.text.secondary'),
    track: tokenVar('colors.light.bg.input'),
    range: tokenVar('colors.light.interactive.toggle-on'),
    thumb: tokenVar('colors.light.fg.toggle-knob'),
  },
}

export const getSliderTokens = (colorScheme: ColorScheme) => tokens[colorScheme]

export const sliderRootStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '8px',
  width: '100%',
}

export const sliderLabelStyles = (colorScheme: ColorScheme) => ({
  color: getSliderTokens(colorScheme).label,
  textStyle: 'label',
})

const THUMB_SIZE = 14

/**
 * The filled portion is a gradient stop rather than a second element, so the
 * whole control is one native `input[type=range]` — which is what gives it
 * keyboard, touch and screen-reader behaviour for free.
 */
export const sliderInputStyles = (
  colorScheme: ColorScheme,
  percent: number
) => {
  const t = getSliderTokens(colorScheme)
  const thumb = {
    appearance: 'none',
    background: t.thumb,
    border: `2px solid ${t.range}`,
    borderRadius: '50%',
    cursor: 'pointer',
    height: `${THUMB_SIZE}px`,
    width: `${THUMB_SIZE}px`,
  }

  return {
    appearance: 'none',
    background: `linear-gradient(to right, ${t.range} 0%, ${t.range} ${percent}%, ${t.track} ${percent}%, ${t.track} 100%)`,
    borderRadius: 'full',
    cursor: 'pointer',
    height: '4px',
    width: '100%',
    '&:disabled': { cursor: 'not-allowed' },
    '&::-webkit-slider-thumb': thumb,
    '&::-moz-range-thumb': thumb,
    // Firefox paints its own track on top of the background otherwise.
    '&::-moz-range-track': { background: 'transparent' },
  }
}
